import { Chart } from "chart.js";
import "./chartConfig";
import {
  HISTOGRAM_BUCKET_OPTIONS,
  HISTOGRAM_CHART_COLORS,
  formatHistogramBucketTooltip,
} from "./dashboardHistogramChartUtils";
import { resolveStableCmAxisMax } from "./telemetryChartAxis";
import { SHOW_RAINFALL } from "../Constants/featureFlags";
import { getRuntimeWlThresholds, getWlScaleMaxCm } from "./telemetryThresholds";

const getIntervalLabel = (bucket) =>
  HISTOGRAM_BUCKET_OPTIONS.find((option) => option.value === bucket)?.label ??
  bucket ??
  "—";

const CHART_RENDER_WIDTH = 1100;
const CHART_RENDER_HEIGHT = 320;

const formatMetricValue = (value, unit = "") => {
  if (value === 0 || value === "0") return unit ? `0 ${unit}` : "0";
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  const rounded = Number.isInteger(num) ? num : Number(num.toFixed(1));
  return unit ? `${rounded} ${unit}` : String(rounded);
};

const getRowPeriodLabel = (row, bucket) =>
  row.periodLabel ?? formatHistogramBucketTooltip(row.time, bucket);

function buildCombinedChartData(rows, bucket) {
  const dense = rows.length > 120;
  const rainColor = HISTOGRAM_CHART_COLORS.rainfall.fill;
  const rainBorder = HISTOGRAM_CHART_COLORS.rainfall.border;
  const wlColor = HISTOGRAM_CHART_COLORS.waterLevel.border;

  const rainDataset = {
        type: "bar",
        label: "Rainfall",
        data: rows.map((row) => row.rainfallMm ?? 0),
        backgroundColor: `${rainColor}99`,
        borderColor: rainBorder,
        borderWidth: 1,
        borderRadius: dense ? 0 : 3,
        maxBarThickness: dense ? undefined : 28,
        yAxisID: "y",
        order: 2,
      };
  const wlDataset = {
        type: "line",
        label: "Water level",
        data: rows.map((row) =>
          row.waterLevelAvgCm == null ? null : row.waterLevelAvgCm,
        ),
        borderColor: wlColor,
        backgroundColor: "transparent",
        fill: false,
        tension: 0.25,
        borderWidth: 2.5,
        pointRadius: dense ? 0 : 3,
        pointBackgroundColor: wlColor,
        pointBorderColor: "#ffffff",
        pointBorderWidth: 1,
        yAxisID: SHOW_RAINFALL ? "y1" : "y",
        spanGaps: true,
        clip: false,
        order: 1,
      };

  return {
    labels: rows.map((row) => getRowPeriodLabel(row, bucket)),
    datasets: SHOW_RAINFALL ? [rainDataset, wlDataset] : [wlDataset],
  };
}

function buildCombinedPdfChartOptions(pointCount, wlMax = null) {
  return {
    responsive: false,
    animation: false,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: {
        display: true,
        position: "top",
        align: "end",
        labels: {
          boxWidth: 10,
          boxHeight: 10,
          font: { size: 10 },
          usePointStyle: true,
        },
      },
      zoom: {
        zoom: { wheel: { enabled: false }, pinch: { enabled: false } },
        pan: { enabled: false },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          font: { size: 9 },
          maxRotation: pointCount > 24 ? 45 : 30,
          autoSkip: true,
          maxTicksLimit: pointCount > 120 ? 14 : 10,
        },
      },
      y: SHOW_RAINFALL
        ? {
            type: "linear",
            position: "left",
            beginAtZero: true,
            title: { display: true, text: "Rainfall (mm)", font: { size: 10 } },
            ticks: { font: { size: 9 } },
            grid: { color: "rgba(15, 23, 42, 0.08)" },
          }
          : {
            type: "linear",
            position: "left",
            beginAtZero: true,
            min: -Math.max(1, (wlMax ?? 40) * 0.03),
            ...(wlMax != null ? { max: wlMax } : {}),
            title: { display: true, text: "Water level (cm)", font: { size: 10 } },
            ticks: {
              font: { size: 9 },
              callback(value) {
                if (Number(value) < 0) return "";
                return value;
              },
            },
            grid: { color: "rgba(15, 23, 42, 0.08)" },
          },
      y1: {
        display: SHOW_RAINFALL,
        type: "linear",
        position: "right",
        beginAtZero: true,
        min: -Math.max(1, (wlMax ?? 40) * 0.03),
        ...(wlMax != null ? { max: wlMax } : {}),
        title: { display: true, text: "Water level (cm)", font: { size: 10 } },
        ticks: {
          font: { size: 9 },
          callback(value) {
            if (Number(value) < 0) return "";
            return value;
          },
        },
        grid: { drawOnChartArea: false },
      },
    },
    elements: {
      line: { tension: 0.25 },
      point: { radius: pointCount > 120 ? 0 : 3 },
    },
    datasets: {
      bar: { maxBarThickness: pointCount > 120 ? undefined : 28 },
      line: { clip: false },
    },
  };
}

async function renderChartImage(type, data, options) {
  const canvas = document.createElement("canvas");
  canvas.width = CHART_RENDER_WIDTH;
  canvas.height = CHART_RENDER_HEIGHT;
  canvas.style.position = "fixed";
  canvas.style.left = "-9999px";
  document.body.appendChild(canvas);

  try {
    const chart = new Chart(canvas, { type, data, options });
    chart.update("none");
    const dataUrl = chart.toBase64Image("image/png", 1);
    chart.destroy();
    return dataUrl;
  } finally {
    canvas.remove();
  }
}

export async function buildHistogramPdfChartImages(
  rows,
  bucket,
  { confirmedDistPeakCm = null } = {},
) {
  if (!rows.length) {
    return { combined: null };
  }

  const wlMax = Math.max(
    resolveStableCmAxisMax(rows.map((row) => row.waterLevelAvgCm)) ?? 0,
    getWlScaleMaxCm(),
  );

  const combined = await renderChartImage(
    "bar",
    buildCombinedChartData(rows, bucket),
    buildCombinedPdfChartOptions(rows.length, wlMax),
  );

  return { combined };
}

export function buildRainfallAnalysisText(rows, summary, meta) {
  const entity = meta.entityName ?? "the selected area";
  const groupByLabel = meta.groupByLabel ?? "Area";
  const interval =
    meta.intervalLabel ?? getIntervalLabel(meta.bucket ?? "1h");
  const periodStart = meta.from;
  const periodEnd = meta.to;

  if (!rows.length) {
    return `No rainfall telemetry was recorded for ${groupByLabel.toLowerCase()} "${entity}" between ${periodStart} and ${periodEnd}. This may indicate that monitoring devices were inactive, filters excluded all locations, or no precipitation occurred during the selected window.`;
  }

  const increments = rows.map((row) => row.rainfallMm ?? 0);
  const total =
    summary.rainfallTotalMm ??
    increments.reduce((sum, value) => sum + value, 0);

  const rainyIntervals = increments.filter((value) => value > 0).length;
  const maxIncrement = Math.max(...increments, 0);
  const maxIncrementIdx = increments.indexOf(maxIncrement);
  const peakPeriod =
    maxIncrement > 0 ? getRowPeriodLabel(rows[maxIncrementIdx], meta.bucket) : null;
  const firstRainIdx = increments.findIndex((value) => value > 0);
  let lastRainIdx = -1;
  for (let i = increments.length - 1; i >= 0; i -= 1) {
    if (increments[i] > 0) {
      lastRainIdx = i;
      break;
    }
  }
  const dryStreak = (() => {
    let longest = 0;
    let current = 0;
    increments.forEach((value) => {
      if (value <= 0) {
        current += 1;
        longest = Math.max(longest, current);
      } else {
        current = 0;
      }
    });
    return longest;
  })();

  let text = `Rainfall analysis for ${groupByLabel.toLowerCase()} "${entity}" covers ${rows.length} ${interval.toLowerCase()} intervals. `;
  text += `Total rainfall for the reporting period is ${formatMetricValue(total, "mm")}. `;

  if (rainyIntervals === 0) {
    text +=
      "No measurable rainfall was detected in any interval; rainfall remains at zero throughout the entire period, indicating dry conditions across the monitored locations.";
    return text;
  }

  const rainyShare = Math.round((rainyIntervals / rows.length) * 100);
  text += `Precipitation was observed in ${rainyIntervals} interval${rainyIntervals === 1 ? "" : "s"} (${rainyShare}% of the period). `;

  if (firstRainIdx >= 0) {
    text += `The first recorded rainfall occurred during ${getRowPeriodLabel(rows[firstRainIdx], meta.bucket)}. `;
  }
  if (lastRainIdx >= 0 && lastRainIdx !== firstRainIdx) {
    text += `The most recent rainfall interval was ${getRowPeriodLabel(rows[lastRainIdx], meta.bucket)}. `;
  }
  if (peakPeriod && maxIncrement > 0) {
    text += `The heaviest single interval delivered ${formatMetricValue(maxIncrement, "mm")} during ${peakPeriod}. `;
  }

  if (dryStreak > 1) {
    text += `The longest dry spell spanned ${dryStreak} consecutive intervals without measurable rain. `;
  }

  const avgWhenRainy =
    rainyIntervals > 0
      ? increments
          .filter((value) => value > 0)
          .reduce((sum, value) => sum + value, 0) / rainyIntervals
      : 0;
  if (avgWhenRainy > 0) {
    text += `When rain did occur, the average intensity was ${formatMetricValue(avgWhenRainy, "mm")} per rainy interval.`;
  }

  return text.trim();
}

export function buildWaterLevelAnalysisText(rows, summary, meta) {
  const { watchCm, alertCm } = getRuntimeWlThresholds();
  const entity = meta.entityName ?? "the selected area";
  const groupByLabel = meta.groupByLabel ?? "Area";
  const interval =
    meta.intervalLabel ?? getIntervalLabel(meta.bucket ?? "1h");

  const measured = rows
    .map((row, index) => ({ value: row.waterLevelAvgCm, index, row }))
    .filter((entry) => entry.value != null);

  if (!measured.length) {
    return `No water level readings were available for ${groupByLabel.toLowerCase()} "${entity}" during the selected period. Devices may have been offline, unassigned, or telemetry may not have been received for the applied hierarchy filters.`;
  }

  const values = measured.map((entry) => entry.value);
  const peak =
    summary.waterLevelAvgCm ?? Math.max(...values);
  const peakEntry =
    measured.find((entry) => entry.value === peak) ?? measured[0];
  const min = Math.min(...values);
  const minEntry = measured.find((entry) => entry.value === min) ?? measured[0];
  const average = values.reduce((sum, value) => sum + value, 0) / values.length;
  const missingIntervals = rows.length - measured.length;

  let risesAfterRain = 0;
  for (let i = 1; i < rows.length; i += 1) {
    const priorRain = rows[i - 1].rainfallMm ?? 0;
    const prevLevel = rows[i - 1].waterLevelAvgCm;
    const currLevel = rows[i].waterLevelAvgCm;
    if (
      priorRain > 0 &&
      prevLevel != null &&
      currLevel != null &&
      currLevel > prevLevel
    ) {
      risesAfterRain += 1;
    }
  }

  let text = `Water level telemetry for ${groupByLabel.toLowerCase()} "${entity}" is summarized across ${rows.length} ${interval.toLowerCase()} intervals. `;
  text += `Valid readings were received in ${measured.length} interval${measured.length === 1 ? "" : "s"}`;
  if (missingIntervals > 0) {
    text += ` (${missingIntervals} interval${missingIntervals === 1 ? "" : "s"} had no data)`;
  }
  text += ". ";

  text += `The peak water level was ${formatMetricValue(peak, "cm")}, recorded during ${getRowPeriodLabel(peakEntry.row, meta.bucket)}. `;
  if (peak >= alertCm) {
    text += `This exceeds the ${alertCm} cm alert trigger. `;
  } else if (peak >= watchCm) {
    text += `This is in the watch band (${watchCm}–${alertCm} cm). `;
  } else {
    text += `This remains below the ${watchCm} cm watch trigger. `;
  }
  text += `The lowest observed level was ${formatMetricValue(min, "cm")} during ${getRowPeriodLabel(minEntry.row, meta.bucket)}, with an interval average of ${formatMetricValue(average, "cm")}. `;

  const levelRange = peak - min;
  if (levelRange > 0) {
    text += `Overall fluctuation across the period was ${formatMetricValue(levelRange, "cm")} between minimum and maximum readings. `;
  } else {
    text += "Levels remained stable with no measurable variation between intervals. ";
  }

  if (SHOW_RAINFALL && risesAfterRain > 0) {
    text += `Water level increased following rainfall in ${risesAfterRain} interval${risesAfterRain === 1 ? "" : "s"}, suggesting a measurable hydrological response to precipitation within the monitored area. `;
  } else if (SHOW_RAINFALL && (summary.rainfallTotalMm ?? 0) > 0) {
    text +=
      "Despite recorded rainfall, no clear interval-to-interval rise in water level was detected; levels may have plateaued quickly or drainage offset incoming water. ";
  } else if (SHOW_RAINFALL) {
    text +=
      "With little or no rainfall during the period, water level changes are likely driven by baseline flow, infiltration, or sensor variation rather than storm inflow. ";
  }

  const startLevel = measured[0].value;
  const endLevel = measured[measured.length - 1].value;
  const netChange = endLevel - startLevel;
  if (Math.abs(netChange) >= 0.1) {
    const direction = netChange > 0 ? "increased" : "decreased";
    text += `From the first to the last reading, water level ${direction} by ${formatMetricValue(Math.abs(netChange), "cm")}.`;
  } else {
    text += "Net change from the first to the last reading was negligible.";
  }

  return text.trim();
}
